import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Callout, Panel, SkeletonRows, errorText, formatDate, formatDateTime } from "@amber/ui";
import type { DateState, DependenciesResponse } from "../../contracts/index";
import { dependenciesQuery } from "../queries";
import { Name, Table, Td, Th, ToneIcon } from "../budgets/parts";
import { RecordDateDialog, type DateTarget } from "./RecordDateDialog";

const day = (date: string) => formatDate(`${date}T12:00:00Z`);

function DateCell({ date, state, verb, none }: { date: string | null; state: DateState; verb: string; none: string }) {
  if (date === null) return <span className="text-muted">{none}</span>;
  if (state === "ok") return <span className="text-fg">{`${verb} ${day(date)}`}</span>;
  const tone = state === "passed" ? "failed" : "attention";
  return (
    <span
      className={`inline-flex items-start gap-1 font-strong ${tone === "failed" ? "text-attention" : "text-waiting"}`}
    >
      <ToneIcon tone={tone} />
      {state === "passed" ? `Passed ${day(date)}` : `${verb} ${day(date)} · soon`}
    </span>
  );
}

function RecordButton({
  data,
  label,
  target,
  onRecord,
}: {
  data: DependenciesResponse;
  label: string;
  target: DateTarget;
  onRecord: (target: DateTarget) => void;
}) {
  const action = data.actions.record;
  return (
    <Button
      size="sm"
      aria-label={`${label} for ${target.label}`}
      disabledReason={action.enabled ? null : action.reason}
      onClick={() => {
        onRecord(target);
      }}
    >
      {label}
    </Button>
  );
}

function Credentials({ data, onRecord }: { data: DependenciesResponse; onRecord: (t: DateTarget) => void }) {
  return (
    <Panel title="Credentials" note="Names only; values are never stored or shown">
      <Table
        label="Credentials"
        minWidth={720}
        head={
          <>
            <Th>Credential</Th>
            <Th>Configured</Th>
            <Th>Last verified</Th>
            <Th>Expiry</Th>
            <Th>
              <span className="sr-only">Actions</span>
            </Th>
          </>
        }
      >
        {data.credentials.map((c) => (
          <tr key={c.name}>
            <Td>
              <Name detail={c.provider}>
                <span className="font-mono">{c.name}</span>
              </Name>
            </Td>
            <Td>{c.configured ? <span className="text-fg">Yes</span> : "Not configured"}</Td>
            <Td>{c.last_verified_at ? formatDateTime(c.last_verified_at) : "Not verified yet"}</Td>
            <Td>
              <DateCell date={c.expires_on} state={c.expiry} verb="Expires" none="No expiry" />
            </Td>
            <Td right>
              <RecordButton
                data={data}
                label="Record expiry…"
                target={{ kind: "credential", key: c.name, label: c.name, date: c.expires_on, revision: c.revision }}
                onRecord={onRecord}
              />
            </Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function Licences({ data, onRecord }: { data: DependenciesResponse; onRecord: (t: DateTarget) => void }) {
  return (
    <Panel title="Licences and plans">
      <Table
        label="Licences and plans"
        head={
          <>
            <Th>Plan</Th>
            <Th>Renewal</Th>
            <Th>
              <span className="sr-only">Actions</span>
            </Th>
          </>
        }
      >
        {data.licences.map((l) => (
          <tr key={l.key}>
            <Td>
              <Name detail={l.recorded ? `${l.recorded.by}: “${l.recorded.reason}”` : l.provider}>{l.label}</Name>
            </Td>
            <Td>
              <DateCell date={l.renews_on} state={l.renewal} verb="Renews" none="No renewal date" />
            </Td>
            <Td right>
              <RecordButton
                data={data}
                label="Record renewal…"
                target={{ kind: "licence", key: l.key, label: l.label, date: l.renews_on, revision: l.revision }}
                onRecord={onRecord}
              />
            </Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function Concentration({ data }: { data: DependenciesResponse }) {
  return (
    <Panel title="Concentration" note="Last 30 days">
      <Table
        label="Dependency concentration"
        head={
          <>
            <Th>Function</Th>
            <Th>Carried by</Th>
            <Th right>Share</Th>
            <Th>Backup</Th>
          </>
        }
      >
        {data.concentration.map((c) => (
          <tr key={c.function}>
            <Td>
              <span className="font-strong text-fg">{c.label}</span>
            </Td>
            <Td>{c.provider}</Td>
            <Td right className="tabular-nums">
              {c.share === null ? <span title={c.basis}>—</span> : <span title={c.basis}>{`${String(c.share)}%`}</span>}
            </Td>
            <Td>
              {c.backup ?? <span className="font-strong whitespace-nowrap text-waiting">No qualified backup</span>}
            </Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

export function DependenciesPanel({ onRecorded }: { onRecorded?: () => void }) {
  const query = useQuery(dependenciesQuery);
  const [recording, setRecording] = useState<DateTarget | null>(null);
  const data = query.data;
  return (
    <section id="dependencies" aria-labelledby="dependencies-title" className="px-4 pt-4 pb-6 max-compact:px-2">
      <h2 id="dependencies-title" className="m-0 mb-1 text-lg font-semibold text-fg">
        Dependencies
      </h2>
      <p className="m-0 mb-3 text-md text-muted">
        Credentials, plans and how much of each function rests on one provider. Dates within{" "}
        {data ? String(data.window_days) : "the warning window of"} days are highlighted and open a task.
      </p>
      {query.isError ? (
        <Callout tone="attention" role="alert">
          Couldn&apos;t load the dependencies. {errorText(query.error)}
        </Callout>
      ) : null}
      {data ? (
        <div className="grid gap-3">
          <Credentials data={data} onRecord={setRecording} />
          <Licences data={data} onRecord={setRecording} />
          <Concentration data={data} />
        </div>
      ) : null}
      {query.isPending ? <SkeletonRows columns={4} rows={4} /> : null}
      {recording ? (
        <RecordDateDialog
          target={recording}
          onClose={() => {
            setRecording(null);
          }}
          onRecorded={onRecorded}
        />
      ) : null}
    </section>
  );
}
